import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Button, CalendarPicker, NavBar, Popup, SearchBar, Skeleton, Toast} from "antd-mobile";
import {DownOutline, SearchOutline} from 'antd-mobile-icons';
import {getNavBarContainerStyle} from "../../../utils/deviceUtils";
import {isAndroid} from "../../../utils/deviceUtils";
import {useQuery} from '@apollo/client';
import DeliveredCardTemporary, { DeliveredOrder } from "./DeliveredCardTemporary";
import EmptyState from "../../../components/EmptyState";
import {getTimestampsFromDate, isSameDay, timestampToDate} from "utils/dateTimeUtils";
import {DeliveryTaskOrderListForDeliveredResponse, GET_DELIVERY_TASK_ORDER_LIST_FOR_DELIVERED} from "./deliveryTaskOrderListTemporary";
import {t} from "i18n";
import {formatLocalizedDate} from "utils/dateFormatUtils";
import {DeliveredParcelIcon} from "assets/icons";
import accessCodeSvg from "../../../assets/icons/svgs/accessCode.svg";
import { DeliveryDetailPath } from "../../../enums/DeliveryNavigatePathEnum";
import { getAccessCodesByOrderId } from "../graphql/queries/accessCodeQueries";
import { useAccessCodeFeature } from "../delivering/hooks/useAccessCodeFeature";


interface DeliveredProps {
}

const DeliveredPage: React.FC<DeliveredProps> = () => {
    const navigate = useNavigate();
    const mainContainerRef = useRef<HTMLDivElement>(null);
    const [showCalendar, setShowCalendar] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [searchValue, setSearchValue] = useState<string>('');
    const [accessLoadingOrderId, setAccessLoadingOrderId] = useState<number | null>(null);
    const { isEnabled: isAccessFeatureEnabled, loading: isCheckingAccessFeature } = useAccessCodeFeature();

    console.log('DeliveredTemporaryPage');
    
    // 从 sessionStorage 恢复日期筛选状态
    const getInitialDate = (): Date | null => {
        const savedDate = sessionStorage.getItem('deliveredPageSelectedDate');
        if (savedDate) {
            try {
                const date = new Date(savedDate);
                if (!isNaN(date.getTime())) {
                    return date;
                }
            } catch (e) {
                // ignore
            }
        }
        return new Date(); // 默认选中当天
    };
    
    const [selectedDate, setSelectedDate] = useState<Date | null>(getInitialDate());

    // 从selectedDate计算时间戳传递给后端接口
    const { startTimestamp, endTimestamp } = selectedDate ? 
        getTimestampsFromDate(selectedDate) : 
        getTimestampsFromDate(new Date());

    // 使用 useQuery 直接调用后端接口
    const { data, loading, refetch } = useQuery<DeliveryTaskOrderListForDeliveredResponse>(GET_DELIVERY_TASK_ORDER_LIST_FOR_DELIVERED, {
        variables: {
            maxDeliveryTaskId: 0, // 设置为0以获取所有数据
            startTimestamp: startTimestamp,
            endTimestamp: endTimestamp,
        },
        fetchPolicy: 'cache-and-network', // 有网络时获取最新数据，无网络时使用缓存
        errorPolicy: 'all', // 即使有错误也返回缓存数据
    });

    const deliveryTaskOrderList = data?.getDeliveryTaskOrderListForDelivered?.deliveryTaskOrderList || [];
    const sameAddressDiscountParcelCount =
        data?.getDeliveryTaskOrderListForDelivered?.sameAddressDiscountParcelCount || 0;

    const handleAccessEntryClick = useCallback(async (order: DeliveredOrder, isGrouped: boolean) => {
        if (!order.orderId) {
            Toast.show({
                content: t('pages.delivery.orderDetail.orderIdRequired'),
                position: 'bottom'
            });
            return;
        }

        try {
            setAccessLoadingOrderId(order.id);
            const accessCodes = await getAccessCodesByOrderId(order.orderId);
            const displayAddress = isGrouped
                ? (order.standardizedAddress || order.address)
                : order.address;
            if (accessCodes.length > 0) {
                navigate(
                    DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL_ACCESS_INFO.replace(
                        ':trackingNumber',
                        order.trackingNumber
                    ),
                    {
                        state: {
                            orderId: order.orderId,
                            addressId: order.addressId,
                            trackingNumber: order.trackingNumber,
                            address: displayAddress,
                            displayAddress: displayAddress,
                            addressLat: order.addressLat,
                            addressLon: order.addressLon,
                            skipDistanceLimit: true
                        }
                    }
                );
            } else {
                navigate(
                    DeliveryDetailPath.DELIVERY_DELIVERING_ORDER_DETAIL_ADD_ACCESS_CODE.replace(
                        ':trackingNumber',
                        order.trackingNumber
                    ),
                    {
                        state: {
                            orderId: order.orderId,
                            addressId: order.addressId,
                            trackingNumber: order.trackingNumber,
                            address: displayAddress,
                            displayAddress: displayAddress,
                            addressLat: order.addressLat,
                            addressLon: order.addressLon,
                            skipDistanceLimit: true
                        }
                    }
                );
            }
        } catch (error) {
            console.error('Failed to fetch access codes:', error);
            Toast.show({
                content: t('errors.system.networkError'),
                position: 'bottom'
            });
        } finally {
            setAccessLoadingOrderId(null);
        }
    }, [navigate]);

    const renderAccessEntry = useCallback((order: DeliveredOrder, isGrouped: boolean) => {
        if (isCheckingAccessFeature || !isAccessFeatureEnabled) {
            return null;
        }
        const isLoading = accessLoadingOrderId === order.id;
        return (
            <button
                onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    if (!isLoading) {
                        handleAccessEntryClick(order, isGrouped);
                    }
                }}
                disabled={isLoading}
                className="flex items-center gap-1 border border-blue-400 text-blue-500 rounded px-2 py-1 text-xs active:opacity-70"
                aria-label={t('pages.delivery.orderDetail.accessInfo')}
            >
                <span>{t('pages.delivery.orderDetail.accessInfo')}</span>
                <img
                    src={accessCodeSvg}
                    alt="Access Code"
                    className="w-4 h-4"
                />
            </button>
        );
    }, [accessLoadingOrderId, handleAccessEntryClick, isAccessFeatureEnabled, isCheckingAccessFeature]);

    // 当时间戳变化时重新获取数据
    useEffect(() => {
        refetch({
            maxDeliveryTaskId: 0,
            startTimestamp: startTimestamp,
            endTimestamp: endTimestamp,
        });
    }, [startTimestamp, endTimestamp, refetch]);

    const allOrders = useMemo(() => {
        return (deliveryTaskOrderList.filter(item => item?.isDelivered) || [])
            .map(item => {
                const rawAddress = [
                    item.orderWithAddress?.deliveryAddress?.addressLine1 || '',
                    item.orderWithAddress?.deliveryAddress?.addressLine2 || '',
                    item.orderWithAddress?.deliveryAddress?.city || '',
                    item.orderWithAddress?.deliveryAddress?.state || '',
                    item.orderWithAddress?.deliveryAddress?.zipcode || '',
                    'US'
                ].filter(Boolean).join(' ');
                const standardizedAddress = item.orderWithAddress?.deliveryAddress?.standardizedAddressString || '';

                return {
                    id: item.id,
                    trackingNumber: item.trackingNumber || '',
                    deliveryAttempts: item.orderWithAddress?.order?.deliveryAttempts || 0,
                    stopNumber: item.stopNumber,
                    subStopNumber: item.subStopNumber,
                    fullName: item.orderWithAddress?.deliveryAddress?.fullName || '',
                    address: rawAddress,
                    standardizedAddress: standardizedAddress || undefined,
                    routeNumber: item.routeName,
                    deliveredAt: item.orderWithAddress?.order?.deliveredAt || '',
                    expectedDeliveryTime: item.orderWithAddress?.order?.expectedDeliveryTime || '',
                    deliveryType: item?.orderWithAddress?.order?.overallStatus || '',
                    phone: item.orderWithAddress?.deliveryAddress?.phoneNumber || '',
                    taskDate: item.taskDate,
                    addressGroupKey: item.addressGroupKey,
                    orderId: item.orderWithAddress?.order?.id,
                    addressId: item.orderWithAddress?.deliveryAddress?.id,
                    addressLat: item.orderWithAddress?.deliveryAddress?.latitude,
                    addressLon: item.orderWithAddress?.deliveryAddress?.longitude
                };
            });
    }, [deliveryTaskOrderList]);

    const filteredOrders = useMemo(() => {
        const keyword = searchValue.toLowerCase();
        return allOrders
            .filter(order => (keyword === '') || order.trackingNumber.toLowerCase().includes(keyword))
            .filter(order => (selectedDate === null) || isSameDay(timestampToDate(order.deliveredAt), selectedDate));
    }, [allOrders, searchValue, selectedDate]);

    // 根据 taskDate 和 addressGroupKey 分组订单
    // 保持后端返回的顺序，分组时使用 Map 保持插入顺序
    const groupOrdersByTaskDateAndAddress = (orders: DeliveredOrder[]) => {
        const groups = new Map<string, DeliveredOrder[]>();

        orders.forEach(order => {
            // 使用 taskDate 和 addressGroupKey 作为分组键
            // 只有相同 taskDate 和 addressGroupKey 的订单才会聚合在一起
            const groupKey = `${order.taskDate || ''}|${order.addressGroupKey || ''}`;
            if (!groups.has(groupKey)) {
                groups.set(groupKey, []);
            }
            groups.get(groupKey)!.push(order);
        });

        return groups;
    };

    const groupedOrders = useMemo(() => {
        const groups = groupOrdersByTaskDateAndAddress(filteredOrders);
        
        // 将 Map 转换为数组，保持后端返回的顺序，不进行任何排序
        return Array.from(groups.entries());
    }, [filteredOrders]);

    const handleDateSelect = (date: Date | null) => {
        setSelectedDate(date);
    };

    const handleSearch = (value: string) => {
        setSearchValue(value);
    };

    const clearFilters = () => {
        setSearchValue('');
        const today = new Date();
        setSelectedDate(today); // 重置为当天
        sessionStorage.setItem('deliveredPageSelectedDate', today.toISOString());
    };

    // 检查是否与当天不同来判断是否在过滤
    const isFiltering = searchValue !== '' || (selectedDate && !isSameDay(selectedDate, new Date()));

    // 保存日期筛选状态到 sessionStorage
    useEffect(() => {
        if (selectedDate) {
            sessionStorage.setItem('deliveredPageSelectedDate', selectedDate.toISOString());
        } else {
            sessionStorage.removeItem('deliveredPageSelectedDate');
        }
    }, [selectedDate]);

    // 恢复主容器滚动位置
    useEffect(() => {
        if (!loading && filteredOrders.length > 0) {
            const savedScrollTop = sessionStorage.getItem('deliveredPageScrollTop');
            if (savedScrollTop && mainContainerRef.current) {
                setTimeout(() => {
                    if (mainContainerRef.current) {
                        mainContainerRef.current.scrollTop = parseInt(savedScrollTop);
                        sessionStorage.removeItem('deliveredPageScrollTop');
                    }
                }, 200);
            }
        }
    }, [loading, filteredOrders]);

    return (
        <div className="flex flex-col h-screen bg-gray-50">
            <div {...getNavBarContainerStyle()}>
                <NavBar
                onBack={() => navigate(-1)}
                right={
                    <div className="flex items-center justify-end">
                        {isFiltering && (
                            <Button color='primary' fill='none' onClick={clearFilters}>
                                {t(`pages.useMenu.clear`)}
                            </Button>
                        )}

                        <Button
                            color='primary'
                            fill='none'
                            onClick={() => setShowSearch(true)}
                        >
                            <SearchOutline/>
                        </Button>
                    </div>
                }>
                {t(`pages.useMenu.delivered`)}
                </NavBar>
            </div>

            {/* 搜索弹出框 */}
            <Popup
                visible={showSearch}
                onMaskClick={() => setShowSearch(false)}
                position='top'
                bodyStyle={{
                    borderBottomLeftRadius: '0.5em',
                    borderBottomRightRadius: '0.5em',
                    minHeight: '3.75em',
                    padding: '0.75em',
                    top: isAndroid() ? '24px' : '0px' // 为Android设备添加顶部偏移
                }}
            >
                <SearchBar
                    placeholder={t('pages.delivery.delivered.searchPlaceholder')}
                    value={searchValue}
                    onChange={setSearchValue}
                    onSearch={handleSearch}
                    onCancel={() => {
                        setShowSearch(false);
                    }}
                />
            </Popup>

            {/* 日期选择器 */}
            <div className="p-2 bg-white">
                <Button
                    block
                    fill='none'
                    onClick={() => setShowCalendar(true)}
                >
                    <div className="flex items-center justify-center w-full">
                        <span className="mr-1">
                            {selectedDate ? formatLocalizedDate(selectedDate) : t('pages.delivery.delivered.selectDate')}
                        </span>
                        <DownOutline/>
                    </div>
                </Button>
            </div>

            <CalendarPicker
                visible={showCalendar}
                onClose={() => setShowCalendar(false)}
                onConfirm={handleDateSelect}
                defaultValue={selectedDate}
                selectionMode="single"
                min={new Date(2025, 0, 1)} // 设置最小可选日期为2025年1月1日
                max={new Date()} // 设置最大可选日期为今天
                title={t('pages.delivery.delivered.selectDate')}
            />

            {/* 统计总数 - 仅在有订单时显示 */}
            {filteredOrders.length > 0 && (
                <div className="px-4 py-3 bg-white">
                    <div className="flex items-center">
                        <DeliveredParcelIcon width={20} height={20} className="mr-2" />
                        <span className="text-gray-700">{t('pages.delivery.delivered.totalDeliveredParcels')}: </span>
                        <span className="text-black font-bold ml-1">{filteredOrders.length}</span>
                    </div>
                    <div className="mt-1 text-sm text-gray-500">
                        {t('pages.delivery.delivered.sameAddressDiscountParcels', {
                            count: sameAddressDiscountParcelCount
                        })}
                    </div>
                </div>
            )}

            <div 
                id="delivered-main-container"
                ref={mainContainerRef}
                className="flex-1 overflow-y-auto"
            >
                {loading ? (
                    <div className="flex flex-col h-screen">
                        <Skeleton.Title animated />
                        <Skeleton.Paragraph lineCount={4} animated />
                        <Skeleton.Title animated />
                        <Skeleton.Paragraph lineCount={4} animated />
                        <Skeleton.Title animated />
                        <Skeleton.Paragraph lineCount={4} animated />
                        <Skeleton.Title animated />
                        <Skeleton.Paragraph lineCount={4} animated />
                    </div>
                ):(
                    <>
                        {filteredOrders.length > 0 ? (
                            <div className="bg-gray-100 p-2">
                                {groupedOrders.map(([groupKey, ordersInGroup]) => {
                                    if (ordersInGroup.length === 1) {
                                        return (
                                            <DeliveredCardTemporary
                                                key={ordersInGroup[0].id}
                                                order={ordersInGroup[0]}
                                                accessEntry={renderAccessEntry(ordersInGroup[0], false)}
                                            />
                                        );
                                    }

                                    return (
                                        <div
                                            key={groupKey}
                                            className="border-2 border-blue-400 rounded-lg mb-3 relative bg-blue-50"
                                        >
                                            <div className="absolute top-0 left-0 bg-blue-400 text-white text-xs px-2 py-1 rounded-br z-10 whitespace-nowrap">
                                                {t('pages.delivery.delivering.addressGroup', {orderCount: ordersInGroup.length})}
                                            </div>
                                            {ordersInGroup.length > 0 && (
                                                <div className="pt-8 px-2 pb-2">
                                                    {/* 地址在一行展示 */}
                                                    <div className="flex items-start justify-between gap-2 mb-2">
                                                        <p className="text-base font-semibold flex-1 break-words min-w-0">
                                                            {ordersInGroup[0].standardizedAddress || ordersInGroup[0].address}
                                                        </p>
                                                    </div>
                                                    {/* 门禁信息在地址下方，卡片右边展示 */}
                                                    <div className="flex justify-end">
                                                        {renderAccessEntry(ordersInGroup[0], true)}
                                                    </div>
                                                </div>
                                            )}
                                            {ordersInGroup.map((order) => (
                                                <DeliveredCardTemporary
                                                    key={order.id}
                                                    order={order}
                                                    hideAddress={true}
                                                    isGrouped={true}
                                                />
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <EmptyState/>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default DeliveredPage;
